import React, { useState, useMemo } from 'react';
import {
  Paper,
  Box,
  Typography,
  Grid,
  IconButton,
  Chip,
  TextField,
  Tooltip
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Treatment type color mapping
const TYPE_COLORS = {
  'WELLNESS': { bg: '#d1fae5', text: '#059669' },
  'PHYSIOTHERAPY': { bg: '#dbeafe', text: '#2563eb' },
  'DOCTOR CONSULTATION': { bg: '#e9d5ff', text: '#9333ea' },
  'THERAPY': { bg: '#ccfbf1', text: '#0d9488' },
  'AYURVEDA': { bg: '#fed7aa', text: '#ea580c' },
  'FITNESS': { bg: '#fecaca', text: '#dc2626' },
  'NUTRITION': { bg: '#fef3c7', text: '#d97706' },
  'DEFAULT': { bg: '#e5e7eb', text: '#374151' }
};

const getTypeColor = (type) => {
  if (!type) return TYPE_COLORS.DEFAULT;
  const upperType = type.toUpperCase();
  return TYPE_COLORS[upperType] || TYPE_COLORS.DEFAULT;
};

// Format time
const formatTime = (time) => {
  if (time === null || time === undefined) return 'TBD';
  if (typeof time === 'string') return time;
  if (typeof time === 'number' && !isNaN(time)) {
    const hours = Math.floor(time / 60);
    const mins = time % 60;
    return `${hours}:${String(mins).padStart(2, '0')}`;
  }
  return 'TBD';
};

const PackageSchedulingCalendarView = ({
  schedule,
  bookingResults,
  editingTreatment,
  setEditingTreatment,
  treatmentInstructions,
  handleTreatmentInstructionChange
}) => {
  const [weekOffset, setWeekOffset] = useState(0);

  // Group days into weeks (7 days each)
  const weeks = useMemo(() => {
    if (!schedule || !schedule.schedule) return [];
    
    const allDays = schedule.schedule;
    const weekGroups = [];
    
    for (let i = 0; i < allDays.length; i += 7) {
      weekGroups.push(allDays.slice(i, i + 7));
    }
    
    return weekGroups;
  }, [schedule]);

  const currentWeek = weeks[weekOffset] || [];
  const hasNextWeek = weekOffset < weeks.length - 1;
  const hasPrevWeek = weekOffset > 0;

  const getTreatmentStatus = (treatment, date) => {
    const booking = bookingResults?.bookings?.find((b) => {
      if (b.date !== date) return false;
      if (treatment.inclusionId && b.inclusionId) return b.inclusionId === treatment.inclusionId;
      return b.treatmentName === treatment.name;
    });
    const failure = bookingResults?.failures?.find((f) => {
      if (f.date !== date) return false;
      if (treatment.inclusionId && f.inclusionId) return f.inclusionId === treatment.inclusionId;
      return f.treatmentName === treatment.name;
    });
    return {
      isBooked: !!booking,
      isFailed: !!failure,
      failureReason: failure?.reason || '',
      failureType: failure?.type || ''
    };
  };

  return (
    <Box>
      {/* Week Navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <IconButton 
          onClick={() => setWeekOffset(prev => prev - 1)}
          disabled={!hasPrevWeek}
          sx={{ 
            color: hasPrevWeek ? '#E4C984' : '#ccc',
            '&:hover': { backgroundColor: hasPrevWeek ? 'rgba(228, 201, 132, 0.1)' : 'transparent' }
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
        
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Week {weekOffset + 1} of {weeks.length}
          {currentWeek.length > 0 && (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              ({currentWeek[0]?.date} - {currentWeek[currentWeek.length - 1]?.date})
            </Typography>
          )}
        </Typography>
        
        <IconButton 
          onClick={() => setWeekOffset(prev => prev + 1)}
          disabled={!hasNextWeek}
          sx={{ 
            color: hasNextWeek ? '#E4C984' : '#ccc',
            '&:hover': { backgroundColor: hasNextWeek ? 'rgba(228, 201, 132, 0.1)' : 'transparent' }
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {/* Calendar Grid */}
      <Grid container spacing={1.5}>
        {currentWeek.map((day, dayIndex) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={day.day}>
            <Paper 
              elevation={2}
              sx={{ 
                p: 2,
                height: '100%',
                minHeight: 300,
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid #e5e7eb',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  borderColor: '#E4C984'
                }
              }}
            >
              {/* Day Header */}
              <Box sx={{ mb: 2, pb: 1.5, borderBottom: '2px solid #E4C984' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#171717' }}>
                  Day {day.day}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {day.dayOfWeek}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {day.date}
                </Typography>
              </Box>

              {/* Treatments List */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto' }}>
                {day.treatments.length === 0 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
                    No treatments scheduled
                  </Typography>
                ) : (
                  [...day.treatments]
                    .sort((a, b) => {
                      // Sort by startTime if both have it (chronological order)
                      if (a.startTime && b.startTime) {
                        // Handle both string format (HH:MM) and number format (minutes)
                        const getMinutes = (time) => {
                          if (typeof time === 'number') return time;
                          if (typeof time === 'string') {
                            const [hours, mins] = time.split(':').map(Number);
                            return hours * 60 + mins;
                          }
                          return 0;
                        };
                        return getMinutes(a.startTime) - getMinutes(b.startTime);
                      }
                      // If only one has startTime, put it first
                      if (a.startTime) return -1;
                      if (b.startTime) return 1;
                      // Otherwise sort by order number
                      return (a.order || 0) - (b.order || 0);
                    })
                    .map((treatment, sortedIdx) => {
                    let rowTreatmentIndex = day.treatments.findIndex(
                      (t) => treatment.inclusionId && t.inclusionId === treatment.inclusionId
                    );
                    if (rowTreatmentIndex < 0) {
                      rowTreatmentIndex = day.treatments.findIndex(
                        (t) =>
                          t.name === treatment.name &&
                          Number(t.order ?? 0) === Number(treatment.order ?? 0)
                      );
                    }
                    if (rowTreatmentIndex < 0) rowTreatmentIndex = sortedIdx;
                    const status = getTreatmentStatus(treatment, day.date);
                    const colors = getTypeColor(treatment.type);
                    
                    return (
                      <Paper
                        key={treatment.inclusionId || rowTreatmentIndex}
                        elevation={0}
                        sx={{
                          p: 1.5,
                          border: '1px solid',
                          borderColor: status.isBooked ? '#6ee7b7' : '#e5e7eb',
                          backgroundColor: status.isBooked ? '#f0fdf4' : '#fafafa',
                          '&:hover': {
                            backgroundColor: status.isBooked ? '#dcfce7' : '#f3f4f6',
                            borderColor: status.isBooked ? '#4ade80' : '#E4C984'
                          },
                          cursor: 'default'
                        }}
                      >
                        {/* Treatment Name */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem', flex: 1, pr: 1 }}>
                            {treatment.name}
                          </Typography>
                          {status.isBooked && (
                            <Tooltip title="Booked">
                              <CheckCircleIcon sx={{ fontSize: 16, color: '#059669' }} />
                            </Tooltip>
                          )}
                          {status.isFailed && (
                            <Tooltip title={status.failureReason || 'Booking failed'} arrow>
                              {status.failureType === 'arrival_day_skip' || status.failureType === 'departure_day_skip' ? (
                                <InfoOutlinedIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                              ) : (
                                <ErrorOutlineIcon sx={{ fontSize: 16, color: '#dc2626' }} />
                              )}
                            </Tooltip>
                          )}
                        </Box>

                        {/* Failure/Skip Reason */}
                        {status.isFailed && status.failureReason && (
                          <Box 
                            sx={{ 
                              mt: 0.5, 
                              mb: 0.5,
                              p: 0.5, 
                              bgcolor: status.failureType === 'arrival_day_skip' || status.failureType === 'departure_day_skip' 
                                ? 'rgba(245, 158, 11, 0.1)' 
                                : 'rgba(220, 38, 38, 0.1)', 
                              borderRadius: 0.5,
                              border: '1px solid',
                              borderColor: status.failureType === 'arrival_day_skip' || status.failureType === 'departure_day_skip' 
                                ? 'rgba(245, 158, 11, 0.3)' 
                                : 'rgba(220, 38, 38, 0.3)'
                            }}
                          >
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                fontSize: '0.7rem',
                                color: status.failureType === 'arrival_day_skip' || status.failureType === 'departure_day_skip' 
                                  ? '#92400e' 
                                  : '#7f1d1d',
                                display: 'block'
                              }}
                            >
                              {status.failureType === 'arrival_day_skip' || status.failureType === 'departure_day_skip' ? '⏭️ ' : '❌ '}
                              {status.failureReason}
                            </Typography>
                          </Box>
                        )}

                        {/* Type Badge */}
                        <Chip 
                          label={treatment.type || 'OTHER'} 
                          size="small"
                          sx={{
                            backgroundColor: colors.bg,
                            color: colors.text,
                            fontSize: '0.65rem',
                            height: '18px',
                            mb: 0.5
                          }}
                        />

                        {/* Time & Duration */}
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          ⏰ {formatTime(treatment.startTime)} • {treatment.duration} min
                        </Typography>

                        {/* Tier Level */}
                        {treatment.tier_level && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            Tier: {treatment.tier_level}
                          </Typography>
                        )}

                        {/* Instructions (if any) */}
                        {treatmentInstructions[treatment.inclusionId] && (
                          <Box sx={{ mt: 1, p: 0.5, bgcolor: 'rgba(251, 191, 36, 0.1)', borderRadius: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                              📝 {treatmentInstructions[treatment.inclusionId]}
                            </Typography>
                          </Box>
                        )}

                        {/* Edit Icon (for future use) */}
                        {!status.isBooked && (
                          <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'flex-end' }}>
                            <IconButton
                              size="small"
                              onClick={() => setEditingTreatment({ 
                                dayIndex: (weekOffset * 7) + dayIndex, 
                                treatmentIndex: rowTreatmentIndex 
                              })}
                              sx={{
                                padding: '2px',
                                color: '#E4C984',
                                '&:hover': { backgroundColor: 'rgba(228, 201, 132, 0.1)' }
                              }}
                            >
                              <EditOutlinedIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                        )}
                      </Paper>
                    );
                  })
                )}
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Summary Footer */}
      <Box sx={{ mt: 3, p: 2, bgcolor: '#f9fafb', borderRadius: 1, border: '1px solid #e5e7eb' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary">
              Total Days:
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {schedule?.schedule?.length || 0}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary">
              Total Treatments:
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {schedule?.totalTreatments || 0}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary">
              Week Progress:
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {weekOffset + 1} / {weeks.length}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {currentWeek.length === 0 && (
        <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body2">No days in this week</Typography>
        </Box>
      )}
    </Box>
  );
};

export default PackageSchedulingCalendarView;

